import { t } from 'i18n'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: grid;
  position: relative;
`

const StyledLabel = styled.label`
  margin-bottom: 0.875rem;
  font-weight: 300;
  font-size: 0.875rem;
`

const StyledButton = styled.button<{ $border: boolean; $isOpen: boolean }>`
  padding: ${({ $border }) => ($border ? `1rem` : `0`)};
  border-radius: 5px;
  font-weight: 300;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  background-image: url('/arrow-down-thin.png');
  background-repeat: no-repeat;
  background-position-x: 95%;
  background-position-y: 50%;
  border: ${({ $border, theme }) =>
    $border ? `1px solid ${theme.colors.gray}` : `1px solid transparent`};
  color: ${({ theme }) => theme.colors.lightGray};
  outline: none;
  cursor: pointer;
  padding-right: 25px;
  text-align: left;
  width: 100%;
  ${({ $isOpen }) =>
    $isOpen &&
    `
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}
`

const DropdownPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-top: none;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  input[type='checkbox'] {
    margin-right: 0.75rem;
    cursor: pointer;
  }

  span {
    font-weight: 300;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.lightGray};
  }
`

export interface MultiSelectItem {
  label: string
  value: string
}

interface MultiSelectFilterProps {
  name: string
  title?: string
  items: MultiSelectItem[]
  values: string[]
  onChange: (selectedValues: string[]) => void
  $border?: boolean
  className?: string
}

const MultiSelectFilter = ({
  items,
  name,
  title,
  values,
  onChange,
  $border = true,
  className
}: MultiSelectFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleCheckboxChange = (value: string) => {
    if (value === 'ALL') {
      // Toggle all - if currently all selected, deselect all; otherwise select all
      if (values.length === 0) {
        onChange(items.filter(item => item.value !== 'ALL').map(item => item.value))
      } else {
        onChange([])
      }
    } else {
      if (values.includes(value)) {
        // Remove from selection
        onChange(values.filter(v => v !== value))
      } else {
        // Add to selection
        onChange([...values, value])
      }
    }
  }

  const getButtonText = () => {
    if (values.length === 0) {
      return t('all')
    }
    if (values.length === 1) {
      const selectedItem = items.find(item => item.value === values[0])
      return selectedItem?.label || '1 selected'
    }
    return `${values.length} selected`
  }

  const isAllSelected = values.length === 0

  return (
    <Container className={className} ref={containerRef}>
      {title && <StyledLabel htmlFor={name}>{title}</StyledLabel>}
      <StyledButton
        type="button"
        $border={$border}
        $isOpen={isOpen}
        onClick={handleToggle}
        id={name}
      >
        {getButtonText()}
      </StyledButton>
      <DropdownPanel $isOpen={isOpen}>
        <CheckboxItem key="all">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleCheckboxChange('ALL')}
          />
          <span>{t('all')}</span>
        </CheckboxItem>
        {items
          .filter(item => item.value !== 'ALL')
          .map(item => (
            <CheckboxItem key={item.value}>
              <input
                type="checkbox"
                checked={values.includes(item.value)}
                onChange={() => handleCheckboxChange(item.value)}
              />
              <span>{item.label}</span>
            </CheckboxItem>
          ))}
      </DropdownPanel>
    </Container>
  )
}

export default MultiSelectFilter
